import React from "react";

const Diversity = () => {
    return (
        <div className="w-full text-gray-800">

            {/* HERO */}
            <section className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <span className="uppercase text-sm opacity-80">Company Overview</span>
                    <h1 className="text-4xl md:text-5xl font-bold mt-4 max-w-4xl">
                        Diversity, Equity, Inclusion and Belonging
                    </h1>
                    <p className="mt-6 max-w-3xl text-gray-200">
                        DEIB means embracing the unique backgrounds, perspectives, and
                        talents of every individual. At Persistent, we strive to create an
                        inclusive culture where everyone’s potential flourishes.
                    </p>
                </div>
            </section>

            {/* VIDEO SECTION */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-2xl font-semibold mb-4">
                            Diversity Equity and Inclusion
                        </h2>
                        <p className="text-gray-600 mb-4">
                            At Persistent, we are committed to building a workplace where
                            diversity is celebrated and inclusion is practiced every day.
                        </p>
                        <div className="flex gap-4 text-sm font-medium text-orange-500">
                            <span>#inclusion</span>
                            <span>#hiristhech</span>
                            <span>#jobs</span>
                            <span>#hiring</span>
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src="/images/diversity/video-cover.jpg"
                            alt="DEI Video"
                            className="rounded-lg"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl">
                                ▶
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4Es FRAMEWORK */}
            <section className="bg-gray-50 py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-10">4Es – Framework</h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            {
                                title: "We Enable",
                                desc: "Creating opportunities and an environment where everyone can thrive.",
                            },
                            {
                                title: "We Employ",
                                desc: "Building a diverse workforce across roles and geographies.",
                            },
                            {
                                title: "We Empower",
                                desc: "Equipping people with resources, mentoring, and leadership support.",
                            },
                            {
                                title: "We Engage",
                                desc: "Encouraging participation, collaboration, and inclusion every day.",
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-6 rounded shadow-sm">
                                <h4 className="font-semibold mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-600">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ERGs */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-10">
                        Employee Resource Groups
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                title: "Prerana (Inspiration)",
                                desc: "Promoting awareness and momentum for inclusion at Persistent.",
                            },
                            {
                                title: "Pehchaan (Identity)",
                                desc: "Supporting LGBTQIA+ inclusion and allyship across the organization.",
                            },
                            {
                                title: "ABLE",
                                desc: "Alliance for Belonging, Learning and Empowerment for persons with disabilities.",
                            },
                        ].map((erg, i) => (
                            <div key={i} className="border p-6 rounded">
                                <h4 className="font-semibold mb-2">{erg.title}</h4>
                                <p className="text-sm text-gray-600">{erg.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DIVERSITY METRICS */}
            <section className="bg-gray-900 text-white py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-10">
                        Diversity at Persistent
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { value: "26,000+", label: "Global Workforce" },
                            { value: "29.85%", label: "Women Employees" },
                            { value: "59", label: "Nationalities" },
                            { value: "18", label: "Countries" },
                        ].map((stat, i) => (
                            <div
                                key={i}
                                className="bg-gray-800 p-6 rounded text-center"
                            >
                                <p className="text-2xl font-bold text-orange-400">
                                    {stat.value}
                                </p>
                                <p className="text-sm mt-2">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUOTE */}
            <section className="bg-white py-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="italic text-gray-600 text-lg">
                        “It is essential to value and respect difference to have a truly
                        inclusive culture within the team.”
                    </p>
                    <p className="mt-4 font-semibold">
                        Keith Parker, SVP, CSR, L&D, Media, Communications, and Brand
                    </p>
                </div>
            </section>

            {/* REPORTS */}
            <section className="bg-gray-50 py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-10">
                        Reports and Analytics
                    </h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="bg-white p-4 rounded shadow-sm">
                                <img
                                    src={`/images/diversity/report-${i}.jpg`}
                                    alt="Report"
                                    className="mb-4 rounded"
                                />
                                <p className="text-sm font-medium">
                                    ESG Report {2020 + i}
                                </p>
                                <span className="text-orange-500 text-sm">
                                    Read the full report →
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* JOIN US */}
            <section className="bg-orange-500 text-white py-16 px-6 text-center">
                <h2 className="text-3xl font-semibold mb-4">Join us!</h2>
                <p className="mb-6">Where great ideas rise to the challenge.</p>
                <button className="bg-white text-orange-500 px-6 py-3 rounded font-medium">
                    View Available Positions
                </button>
            </section>

            {/* CONTACT */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-10">Contact us</h2>
                    <form className="grid md:grid-cols-3 gap-6">
                        <input className="border p-3 rounded" placeholder="First name*" />
                        <input className="border p-3 rounded" placeholder="Last name*" />
                        <input className="border p-3 rounded" placeholder="Work email*" />
                        <input className="border p-3 rounded" placeholder="Company*" />
                        <input className="border p-3 rounded" placeholder="Job title*" />
                        <textarea
                            className="md:col-span-3 border p-3 rounded"
                            rows="4"
                            placeholder="How can we help you?"
                        />
                        <button className="md:col-span-3 bg-orange-500 text-white py-3 rounded">
                            Submit
                        </button>
                    </form>
                </div>
            </section>

        </div>
    );
};

export default Diversity;
